'use client';

import NextImage, { ImageProps as NextImageProps } from 'next/image';
import React from 'react';

import { cn } from '@/lib/utils';
import { useBoolean } from '@/hooks/utils';
import { Skeleton } from './skeleton';

export interface ImageProps extends Omit<NextImageProps, 'onError' | 'onLoad'> {
  fallbackSrc?: string;
  className?: string;
  showSkeleton?: boolean;
  showBrokenImage?: boolean;
  containerClassName?: string;
  unoptimized?: boolean;
  quality?: number;
}

/**
 * Enhanced Image component with loading and error states
 */
const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      containerClassName,
      src,
      alt,
      fallbackSrc = '/images/fallback/placeholder.svg',
      showSkeleton = true,
      showBrokenImage = true,
      unoptimized = false,
      placeholder,
      blurDataURL,
      quality = 75,
       ...props
    },
    ref
  ) => {
    const { value: isLoading, onFalse: handleLoad } = useBoolean(true);
    const { value: hasError, onTrue: handleError } = useBoolean(false);

    // If src is null, undefined, or empty string, treat it as an error case
    // and use the fallback image instead
    const isSrcMissing = src === null || src === undefined || src === '';
    const effectiveSrc = isSrcMissing ? fallbackSrc : src;
    const effectiveHasError = isSrcMissing || hasError;
      
  
    if (showBrokenImage === true && effectiveHasError === true && !isSrcMissing) {
      return (
        <div className={cn('relative bg-gray-100 flex items-center justify-center w-full h-full', containerClassName)}>
          <div className="flex flex-col items-center justify-center">
            <NextImage
              src="/images/broken/broken.svg"
              alt={alt || ""}
              width={60}
              height={60}
              className="opacity-70"
              unoptimized={true}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={cn('relative overflow-hidden', containerClassName)}>
        {isLoading === true && showSkeleton === true && placeholder !== 'blur' && (
          <div className="absolute inset-0">
            <Skeleton className="h-full w-full" />
          </div>
        )}
        <NextImage
          ref={ref}
          {...props}
          src={effectiveHasError === true ? fallbackSrc : effectiveSrc}
          alt={alt || ''}
          className={cn(
            'transition-opacity duration-300',
            isLoading === true ? 'opacity-0' : 'opacity-100',
            effectiveHasError === true ? 'grayscale-[50%]' : '',
            className
          )}
          onError={handleError}
          onLoad={handleLoad}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          unoptimized={unoptimized}
          quality={quality}
         />
      </div>
    );
  }
);

Image.displayName = 'Image';

export { Image }; 